import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Camera, User, Building2, Briefcase, Mail, Sparkles } from "lucide-react";
import { UserProfile } from "@/hooks/useProfile";
import { User as SupabaseUser } from "@supabase/supabase-js";

const profileSchema = z.object({
  full_name: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
  company: z.string().optional(),
  role: z.string().optional(),
});

interface ProfileEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: UserProfile | null;
  user: SupabaseUser | null;
  updateProfile: (updates: Partial<UserProfile>) => Promise<{ success: boolean; error?: unknown }>;
  uploadAvatar: (file: File) => Promise<{ success: boolean; url?: string; error?: unknown }>;
  refetch: () => Promise<void>;
}

export function ProfileEditModal({ 
  open, 
  onOpenChange, 
  profile, 
  user, 
  updateProfile, 
  uploadAvatar, 
  refetch 
}: ProfileEditModalProps) {
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  const profileForm = useForm<z.infer<typeof profileSchema>>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      full_name: "",
      company: "",
      role: "",
    },
  });

  // Reset form values whenever modal opens or profile data changes
  useEffect(() => {
    if (open && profile) {
      profileForm.reset({
        full_name: profile.full_name || "",
        company: profile.company || "",
        role: profile.role || "",
      });
    }
  }, [open, profile]);

  const onSubmitProfile = async (values: z.infer<typeof profileSchema>) => {
    setIsUpdatingProfile(true);
    const result = await updateProfile(values);
    if (result.success) {
      await refetch();
      onOpenChange(false);
    }
    setIsUpdatingProfile(false);
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploadingAvatar(true);
    const result = await uploadAvatar(file);
    if (result.success) {
      await refetch();
    }
    setIsUploadingAvatar(false);
  };

  const getInitials = (name?: string, email?: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (email) {
      return email.slice(0, 2).toUpperCase();
    }
    return 'U';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] rounded-2xl border-border/50 shadow-2xl p-0 overflow-hidden">
        {/* Header with gradient accent */}
        <div className="relative bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-8 pt-8 pb-6">
          <div className="absolute top-4 right-4">
            <Sparkles className="h-5 w-5 text-primary/30" />
          </div>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-foreground">Editar Perfil</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Gerencie suas informações pessoais e foto de perfil.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-8 pb-8 space-y-6">
          {/* Avatar Section - Premium Card */}
          <div className="flex items-center gap-5 p-5 bg-muted/30 border border-border/50 rounded-xl shadow-sm">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-br from-primary/20 to-primary/5 rounded-full blur-sm" />
              <Avatar className="relative h-20 w-20 border-2 border-background shadow-lg">
                <AvatarImage src={profile?.avatar_url} />
                <AvatarFallback className="bg-primary text-primary-foreground text-xl font-semibold">
                  {getInitials(profile?.full_name, user?.email)}
                </AvatarFallback>
              </Avatar>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-foreground">Foto do Perfil</h3>
              <p className="text-sm text-muted-foreground mb-3">
                PNG, JPG ou WebP — até 2MB
              </p>
              <input
                type="file"
                id="avatar-upload"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarUpload}
                disabled={isUploadingAvatar}
              />
              <Button 
                variant="outline" 
                size="sm"
                disabled={isUploadingAvatar}
                onClick={() => document.getElementById('avatar-upload')?.click()}
                className="rounded-lg shadow-sm"
              >
                <Camera className="h-4 w-4 mr-2" />
                {isUploadingAvatar ? "Enviando..." : "Alterar Foto"}
              </Button>
            </div>
          </div>

          {/* Profile Form */}
          <Form {...profileForm}>
            <form onSubmit={profileForm.handleSubmit(onSubmitProfile)} className="space-y-5">
              <FormField
                control={profileForm.control}
                name="full_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground font-medium">Nome Completo</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input 
                          placeholder="Digite seu nome completo" 
                          className="pl-10 rounded-lg" 
                          {...field} 
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={profileForm.control}
                name="company"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground font-medium">Empresa</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input 
                          placeholder="Nome da empresa" 
                          className="pl-10 rounded-lg" 
                          {...field} 
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={profileForm.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground font-medium">Cargo</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input 
                          placeholder="Ex: Gerente, Analista..." 
                          className="pl-10 rounded-lg" 
                          {...field} 
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <FormLabel className="text-foreground font-medium">E-mail</FormLabel>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input 
                    value={user?.email || ''} 
                    disabled 
                    className="pl-10 rounded-lg bg-muted/50 text-muted-foreground"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  O e-mail não pode ser alterado
                </p>
              </div>

              <DialogFooter className="pt-4 gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="rounded-lg px-6"
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  disabled={isUpdatingProfile}
                  className="rounded-lg px-6 shadow-md"
                >
                  {isUpdatingProfile ? "Salvando..." : "Salvar Alterações"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
